import {
  type FC,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { Database, Download, Loader2, Printer, X } from "lucide-react";
import { artifactStore } from "../../adapters/artifactStore";
import { ArtifactLoaderContext } from "../../contexts/ArtifactLoaderContext";
import {
  ARTIFACT_TYPE_EXTENSIONS,
  ARTIFACT_TYPE_LABELS,
  type ArtifactType,
  isDocumentArtifactGenerating,
} from "../../types/artifact";
import { MarkdownRenderer } from "./renderers/MarkdownRenderer";
import { CodeRenderer } from "./renderers/CodeRenderer";
import { HtmlRenderer } from "./renderers/HtmlRenderer";
import { SvgRenderer } from "./renderers/SvgRenderer";
import { MermaidRenderer } from "./renderers/MermaidRenderer";
import { SaveToKnowledgeModal } from "./SaveToKnowledgeModal";

const PANEL_WIDTH_DEFAULT = 880;
const PANEL_WIDTH_MIN = 280;
/** Minimum pixels the chat column must keep — mirrors the CSS min-w-[320px] on the chat div. */
const CHAT_MIN_WIDTH = 320;

interface ArtifactPanelProps {
  getDownloadUrl?: (artifactId: string) => string;
}

export const ArtifactPanel: FC<ArtifactPanelProps> = ({ getDownloadUrl }) => {
  const loaderContext = useContext(ArtifactLoaderContext);
  const client = loaderContext?.client;

  const [panelWidth, setPanelWidth] = useState(PANEL_WIDTH_DEFAULT);
  const [isDragging, setIsDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 767px)").matches,
  );
  const panelRef = useRef<HTMLElement>(null) as RefObject<HTMLElement>;
  const resizeStartRef = useRef<{
    x: number;
    w: number;
    pointerId: number;
  } | null>(null);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const sync = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const applyResize = useCallback((clientX: number) => {
    const start = resizeStartRef.current;
    if (!start) return;
    const containerWidth =
      panelRef.current?.parentElement?.clientWidth ?? Infinity;
    const maxWidth =
      containerWidth > 0 ? containerWidth - CHAT_MIN_WIDTH : Infinity;
    const next = Math.max(
      PANEL_WIDTH_MIN,
      Math.min(maxWidth, start.w + (start.x - clientX)),
    );
    setPanelWidth(next);
  }, []);

  const endResize = useCallback((el?: HTMLDivElement, pointerId?: number) => {
    const wasActive = resizeStartRef.current !== null;
    resizeStartRef.current = null;
    if (!wasActive) return;
    setIsDragging(false);
    if (el && pointerId !== undefined && el.hasPointerCapture(pointerId)) {
      try {
        el.releasePointerCapture(pointerId);
      } catch {
        // capture may already be released
      }
    }
    if (typeof document !== "undefined") {
      document.body.style.userSelect = "";
    }
  }, []);

  const handleResizeStart = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      const target = e.currentTarget;
      resizeStartRef.current = {
        x: e.clientX,
        w: panelWidth,
        pointerId: e.pointerId,
      };
      setIsDragging(true);
      // Pointer capture keeps pointermove/pointerup targeted at the handle even
      // when the cursor drifts over child iframes (markdown/html/svg renderers),
      // which is what used to "eat" mouseup and leave the drag stuck.
      try {
        target.setPointerCapture(e.pointerId);
      } catch {
        // setPointerCapture can fail on pointers that were never actually down;
        // safe to ignore — we still fall back to the pointerup handler.
      }
      if (typeof document !== "undefined") {
        document.body.style.userSelect = "none";
      }
    },
    [panelWidth],
  );

  const handleResizeMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const start = resizeStartRef.current;
      if (!start || start.pointerId !== e.pointerId) return;
      applyResize(e.clientX);
    },
    [applyResize],
  );

  const handleResizeEnd = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const start = resizeStartRef.current;
      if (!start || start.pointerId !== e.pointerId) return;
      endResize(e.currentTarget, e.pointerId);
    },
    [endResize],
  );

  // Safety net: if the browser cancels the gesture (alt-tab, OS popup, etc.)
  // or the window loses focus, terminate the drag so the handle doesn't stick.
  useEffect(() => {
    if (!isDragging) return;
    const cancel = () => endResize();
    window.addEventListener("blur", cancel);
    window.addEventListener("contextmenu", cancel);
    return () => {
      window.removeEventListener("blur", cancel);
      window.removeEventListener("contextmenu", cancel);
    };
  }, [isDragging, endResize]);

  const snapshot = useSyncExternalStore(
    (cb) => artifactStore.subscribe(cb),
    () => artifactStore.getSnapshot(),
  );

  const { panelOpen, activeArtifactId, artifactUiPhase } = snapshot;
  const artifact = activeArtifactId
    ? artifactStore.getArtifact(activeArtifactId)
    : undefined;

  const [contentLoading, setContentLoading] = useState(false);
  const fetchedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!artifact || !client || artifact.content || contentLoading) return;
    if (artifact.status !== "complete" || !artifact.chatId) return;
    if (fetchedIdsRef.current.has(artifact.id)) return;

    fetchedIdsRef.current.add(artifact.id);
    setContentLoading(true);

    client
      .getArtifact(artifact.chatId, artifact.id)
      .then((data) => {
        artifactStore.setArtifactContent(artifact.id, data.content ?? "");
      })
      .finally(() => {
        setContentLoading(false);
      });
  }, [
    client,
    artifact?.id,
    artifact?.chatId,
    artifact?.content,
    artifact?.status,
    contentLoading,
  ]);

  useEffect(() => {
    if (!activeArtifactId) fetchedIdsRef.current.clear();
  }, [activeArtifactId]);

  const versions = artifact
    ? artifactStore.getVersionsForIdentifier(artifact.identifier)
    : [];

  const handleClose = () => artifactStore.closePanel();
  const handleVersionSwitch = (id: string) =>
    artifactStore.setActiveArtifact(id);

  const [isDownloading, setIsDownloading] = useState(false);
  const [showSaveToKnowledge, setShowSaveToKnowledge] = useState(false);

  const handleDownloadOriginal = useCallback(async () => {
    if (!artifact) return;
    const ext =
      ARTIFACT_TYPE_EXTENSIONS[artifact.type as ArtifactType] ?? ".txt";
    const filename = `${artifact.title || "artifact"}${ext}`;
    if (artifact.chatId && client) {
      setIsDownloading(true);
      try {
        await client.downloadArtifactContent(
          artifact.chatId,
          artifact.id,
          filename,
        );
      } catch {
        // download errors are non-critical; the user can retry
      } finally {
        setIsDownloading(false);
      }
    } else if (getDownloadUrl) {
      const a = document.createElement("a");
      a.href = getDownloadUrl(artifact.id);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }, [artifact, client, getDownloadUrl]);

  const printHtml = useCallback((html: string) => {
    const iframe = document.createElement("iframe");
    iframe.style.cssText =
      "position:fixed;right:0;bottom:0;width:0;height:0;border:none;opacity:0;pointer-events:none";
    iframe.srcdoc = html;
    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        setTimeout(() => iframe.remove(), 2000);
      }
    };
    document.body.appendChild(iframe);
  }, []);

  /** HTML artifacts: print only (no .html download). Use “Save as PDF” in the browser print dialog if needed. */
  const handlePrintHtml = useCallback(() => {
    if (!artifact?.content || artifact.type !== "text/html") return;
    printHtml(artifact.content);
  }, [artifact, printHtml]);

  const canDownload =
    artifact?.status === "complete" && (!!artifact.chatId || !!getDownloadUrl);

  const isOpen = panelOpen;
  // Show loading only when there's genuinely nothing to display.
  // When a new artifact is pending but we already have a valid artifact
  // (e.g. artifact A from a previous request), keep showing its content
  // instead of flashing a loading skeleton.  The header title already
  // switches to "Creating document…" to indicate the new artifact.
  const isLoading =
    !artifact ||
    (artifact.status === "complete" && !artifact.content) ||
    (artifact.status === "streaming" && !artifact.content);

  return (
    <aside
      ref={panelRef}
      role="complementary"
      aria-label="Artifact panel"
      style={
        isMobile
          ? { zIndex: 50 }
          : {
              width: isOpen ? panelWidth : 0,
              transition: isDragging
                ? undefined
                : "width 280ms ease, opacity 280ms ease",
            }
      }
      className={[
        "cuadra-bg-background cuadra-flex cuadra-flex-col cuadra-overflow-hidden",
        isMobile
          ? `cuadra-absolute cuadra-inset-0 ${isOpen ? "cuadra-opacity-100" : "cuadra-opacity-0 cuadra-pointer-events-none"}`
          : `cuadra-relative cuadra-flex-shrink-0 cuadra-border-l cuadra-border-border ${isOpen ? "cuadra-opacity-100" : "cuadra-opacity-0 cuadra-pointer-events-none"}`,
      ].join(" ")}
    >
      {isOpen && (
        <>
          {/* Drag handle — desktop only; portrait pill indicates resizability */}
          {!isMobile && (
            <div
              role="separator"
              aria-label="Resize artifact panel"
              onPointerDown={handleResizeStart}
              onPointerMove={handleResizeMove}
              onPointerUp={handleResizeEnd}
              onPointerCancel={handleResizeEnd}
              style={{ touchAction: "none" }}
              className="cuadra-absolute cuadra-left-0 cuadra-top-0 cuadra-bottom-0 cuadra-w-4 cuadra-cursor-col-resize cuadra-z-10 cuadra-flex cuadra-items-center cuadra-justify-center"
            >
              <div className="cuadra-w-[3px] cuadra-h-5 cuadra-rounded-full cuadra-bg-[rgba(0,0,0,0.15)] dark:cuadra-bg-white/20" />
            </div>
          )}

          {/* Header — theme-adapting so it matches the rest of the chat */}
          <div className="cuadra-flex cuadra-items-center cuadra-gap-2 cuadra-pl-6 cuadra-pr-3 cuadra-py-3 cuadra-border-b cuadra-border-border cuadra-min-h-[52px] cuadra-bg-card">
            {/* Title + inline download */}
            <div className="cuadra-flex cuadra-items-center cuadra-gap-1.5 cuadra-flex-1 cuadra-min-w-0">
              <h3 className="cuadra-font-brand cuadra-text-sm cuadra-font-normal cuadra-text-foreground cuadra-truncate">
                {!artifact
                  ? isDocumentArtifactGenerating(artifactUiPhase)
                    ? "Generating document…"
                    : "Creating document…"
                  : (artifact.title ?? "Document")}
              </h3>

              {artifact?.status === "complete" &&
                artifact.type === "text/html" &&
                !!artifact.content && (
                  <button
                    type="button"
                    onClick={handlePrintHtml}
                    className="cuadra-flex cuadra-items-center cuadra-gap-0.5 cuadra-flex-shrink-0 cuadra-p-1 cuadra-rounded cuadra-text-muted-foreground hover:cuadra-text-foreground hover:cuadra-bg-muted cuadra-transition-colors cuadra-font-brand"
                    aria-label="Print or save as PDF"
                    title="Print (use Save as PDF in the print dialog)"
                  >
                    <Printer size={13} />
                  </button>
                )}

              {canDownload && artifact?.type !== "text/html" && (
                <button
                  type="button"
                  onClick={() => void handleDownloadOriginal()}
                  disabled={isDownloading}
                  className="cuadra-flex cuadra-items-center cuadra-gap-0.5 cuadra-flex-shrink-0 cuadra-p-1 cuadra-rounded cuadra-text-muted-foreground hover:cuadra-text-foreground hover:cuadra-bg-muted cuadra-transition-colors disabled:cuadra-opacity-50 cuadra-font-brand"
                  aria-label="Download original file"
                  title="Download"
                >
                  {isDownloading ? (
                    <Loader2 size={13} className="cuadra-animate-spin" />
                  ) : (
                    <Download size={13} />
                  )}
                </button>
              )}

              {artifact?.status === "complete" &&
                !!artifact?.content &&
                client && (
                  <button
                    type="button"
                    onClick={() => setShowSaveToKnowledge(true)}
                    className="cuadra-flex cuadra-items-center cuadra-gap-1 cuadra-flex-shrink-0 cuadra-px-2 cuadra-py-1 cuadra-rounded cuadra-text-xs cuadra-font-medium cuadra-text-muted-foreground hover:cuadra-text-foreground hover:cuadra-bg-muted cuadra-transition-colors cuadra-font-brand"
                    aria-label="Save to Knowledge"
                    title="Save artifact to a dataset"
                  >
                    <Database size={12} className="cuadra-flex-shrink-0" />
                    <span>Save to Knowledge</span>
                  </button>
                )}

              {artifact?.status === "complete" && versions.length > 1 && (
                <VersionSelector
                  versions={versions}
                  activeId={artifact.id}
                  onSwitch={handleVersionSwitch}
                />
              )}
            </div>

            {/* Close */}
            <button
              type="button"
              onClick={handleClose}
              className="cuadra-flex-shrink-0 cuadra-p-1.5 cuadra-rounded-lg cuadra-text-muted-foreground hover:cuadra-text-foreground hover:cuadra-bg-muted cuadra-transition-colors"
              aria-label="Close artifact panel"
            >
              <X size={15} />
            </button>
          </div>

          {/* Content — theme-adapting canvas so the white document floats */}
          <div className="cuadra-flex-1 cuadra-min-h-0 cuadra-overflow-auto cuadra-bg-muted">
            {artifact?.status === "error" && (
              <div className="cuadra-p-4 cuadra-text-sm cuadra-text-red-500">
                {artifact.error ??
                  "An error occurred while creating this artifact."}
              </div>
            )}

            {isLoading && <LoadingSkeleton />}

            {!isLoading && artifact?.content && (
              <ArtifactContent
                type={artifact.type}
                content={artifact.content}
                language={artifact.language}
              />
            )}
          </div>
        </>
      )}

      {showSaveToKnowledge && artifact && client && (
        <SaveToKnowledgeModal
          artifact={{
            id: artifact.id,
            title: artifact.title,
            type: artifact.type,
            content: artifact.content ?? "",
            chatId: artifact.chatId,
          }}
          client={client}
          onClose={() => setShowSaveToKnowledge(false)}
        />
      )}
    </aside>
  );
};

// ── Loading spinner ───────────────────────────────────────────────

const LoadingSkeleton: FC = () => (
  <div className="cuadra-flex cuadra-items-center cuadra-justify-center cuadra-h-full cuadra-min-h-[240px]">
    <Loader2 className="cuadra-h-7 cuadra-w-7 cuadra-animate-spin cuadra-text-muted-foreground" />
  </div>
);

// ── Content router ────────────────────────────────────────────────

const ArtifactContent: FC<{
  type: ArtifactType;
  content: string;
  language: string | null;
  downloadUrl?: string;
}> = ({ type, content, language, downloadUrl }) => {
  switch (type) {
    case "text/markdown":
      return <MarkdownRenderer content={content} />;
    case "application/code":
      return <CodeRenderer content={content} language={language} />;
    case "text/html":
      return <HtmlRenderer content={content} downloadUrl={downloadUrl} />;
    case "image/svg+xml":
      return <SvgRenderer content={content} />;
    case "application/vnd.mermaid":
      return <MermaidRenderer content={content} />;
    default:
      return (
        <pre className="cuadra-p-4 cuadra-text-sm cuadra-font-mono cuadra-overflow-auto cuadra-whitespace-pre-wrap">
          {content}
        </pre>
      );
  }
};

// ── Sub-components ────────────────────────────────────────────────

export const ArtifactTypeBadge: FC<{ type: ArtifactType }> = ({ type }) => (
  <span className="cuadra-inline-flex cuadra-items-center cuadra-px-1.5 cuadra-py-0.5 cuadra-rounded cuadra-text-[10px] cuadra-font-medium cuadra-bg-muted cuadra-text-muted-foreground">
    {ARTIFACT_TYPE_LABELS[type] ?? type}
  </span>
);

const VersionSelector: FC<{
  versions: { id: string; version: number }[];
  activeId: string;
  onSwitch: (id: string) => void;
}> = ({ versions, activeId, onSwitch }) => {
  const active = versions.find((v) => v.id === activeId);
  if (!active || versions.length <= 1) return null;

  return (
    <select
      value={activeId}
      onChange={(e) => onSwitch(e.target.value)}
      className="cuadra-text-[10px] cuadra-bg-transparent cuadra-text-muted-foreground cuadra-border cuadra-border-border cuadra-rounded cuadra-px-1 cuadra-py-0.5"
      aria-label="Select artifact version"
    >
      {versions.map((v, i) => (
        <option key={v.id} value={v.id}>
          v{v.version || i + 1}
        </option>
      ))}
    </select>
  );
};

